import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SearchVisualizers.module.css';
import { generateSortedArray } from '../../lib/arrayGenerator';

const LINEAR_SEARCH_CODE = `function linearSearch(arr, target) {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] === target) {
      return i; 
    }
  }
  return -1; 
}`;

const LinearSearch: React.FC = () => {
  const navigate = useNavigate();
  const [array, setArray] = useState(generateSortedArray(15, 100));
  const [target, setTarget] = useState(array[Math.floor(Math.random() * array.length)]);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [foundIndex, setFoundIndex] = useState<number | null>(null);
  const [highlightedLine, setHighlightedLine] = useState<number | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const stopSearch = useRef(false);
  const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

  const handleStop = useCallback(() => {
    stopSearch.current = true;
    setIsSearching(false);
    setCurrentIndex(null);
    setFoundIndex(null);
    setHighlightedLine(null);
  }, []);

  const handleRandomize = useCallback(() => {
    handleStop();
    const newArray = generateSortedArray(15, 100);
    setArray(newArray);
    setTarget(newArray[Math.floor(Math.random() * newArray.length)]);
  }, [handleStop]);

  useEffect(() => {
    handleRandomize();
    return () => {
      stopSearch.current = true;
    };
  }, [handleRandomize]);

  const runLinearSearch = async () => {
    stopSearch.current = false;
    setCurrentIndex(null);
    setFoundIndex(null);
    setHighlightedLine(2);
    setIsSearching(true);

    let found = false;
    for (let i = 0; i < array.length; i++) {
      if (stopSearch.current) return setIsSearching(false);
      setCurrentIndex(i);
      setHighlightedLine(3);
      await delay(300);

      if (array[i] === target) {
        if (stopSearch.current) return setIsSearching(false);
        setFoundIndex(i);
        setHighlightedLine(4);
        found = true;
        await delay(300);
        break;
      }
    }

    if (!found && !stopSearch.current) {
      setHighlightedLine(7);
    }
    setIsSearching(false);
  };

  const handleGoBack = () => navigate(-1);


return (
    <div className={styles.container}>
      <button onClick={handleGoBack} className={styles.returnButton}>
        &larr; Return
      </button>
      <h1>Linear Search</h1>
      <p className={styles.description}>
        Linear search is the simplest searching algorithm. It sequentially checks each element of the list until a match is found or the whole list has been searched.
      </p>
      <div className={styles.useCase}>
        <h3>When to use it?</h3>
        <ul>
          <li>When the array is <b>not sorted</b>.</li>
          <li>For <b>small data sets</b>, as it is easy to implement.</li>
          <li>When you need to perform a <b>single search</b> on an array.</li>
        </ul>
      </div>
      <div className={styles.timeComplexity}>
        <h3>Time Complexity</h3>
        <p>
          Linear search has a time complexity of <b>O(n)</b>, which means that in the worst case, the algorithm has to check every item in the list to find what you're looking for.
        </p>
      </div>
      <div className={styles.visualization}>
        {array.map((value, index)=>(
          <div
            key={index}
            className={`${styles.bar} ${currentIndex === index ? styles.searching: ''} ${foundIndex === index ? styles.found: ''}`}
          >
            {value}
            </div>
        ))}
      </div>
      <div className={styles.controls}>
        <button onClick={runLinearSearch}>Run Search</button>
        <button onClick={handleRandomize}>Randomize</button>
        <button onClick={handleStop}>Stop</button>
        <input
          type="number"
          value={target}
          onChange={(e) => setTarget(parseInt(e.target.value))}
          placeholder="Enter target value"
        />
      </div>
      <div className={styles.codeDisplay}>
        {LINEAR_SEARCH_CODE.split('\n').map((line, index) => (
          <div
            key={index}
            className={`${styles.codeLine} ${highlightedLine === index + 1 ? styles.highlightedLine : ''}`}
          >
            {line}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LinearSearch;